import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { formatRelativeTime } from "../../../utils";
import { AvatarImage } from "../../components/AvatarImage";
import { getDirectory } from "../../../mentions";
import { firstNameOf } from "../home/CoupleHeader";
import { initials } from "./chatShared";
import type { ChatConversationSummary } from "../../../types";

// WhatsApp-style sent/delivered/read ticks - only for the viewer's own last
// message (senderEmail check below), same three-state icon language as
// MessageBubble.tsx's ReadReceiptIcon (single check = sent, double = at the
// other person's device, double + accent color = actually read).
function LastMessageTicks({ lastMessage }: { lastMessage: NonNullable<ChatConversationSummary["lastMessage"]> }) {
  if (lastMessage.readAt) return <i className="bi bi-check-all chat-receipt-read" aria-hidden="true" />;
  if (lastMessage.deliveredAt) return <i className="bi bi-check-all" aria-hidden="true" />;
  return <i className="bi bi-check" aria-hidden="true" />;
}

const ATTACHMENT_ICON: Record<NonNullable<ChatConversationSummary["lastMessage"]>["attachmentType"] & string, string> = {
  image: "bi-camera-fill",
  video: "bi-camera-video-fill",
  audio: "bi-mic-fill",
};

// Portals directly into #chatListView (previously split between a static
// vanilla heading and a nested #chatListContainer - folded into one, since
// the heading now needs a subtitle + the shared section-heading styling,
// not a bare vanilla <h2>). Visibility is still driven by react-router
// (ChatListRouteScreen.tsx toggles #chatListView on route mount/unmount).
function ChatListBody() {
  const navigate = useNavigate();
  const conversations = useAppState((s) => s.chatConversations);
  const currentUserEmail = useAppState((s) => s.currentUserEmail);
  const container = getVanillaEl<HTMLDivElement>("chatListView");
  if (!container) return null;

  const directory = getDirectory();

  return createPortal(
    <>
      <div className="home-section-heading">
        <h2 className="home-section-title">
          <i className="bi bi-chat-dots-fill me-2 text-pink" aria-hidden="true" />
          Chat
        </h2>
        <span className="home-section-hint">Your private conversations</span>
      </div>

      {!conversations.length ? (
        <p className="small muted mb-0">No conversations yet.</p>
      ) : (
        <div className="chat-list" role="list" aria-label="Conversations">
          {conversations.map((c) => {
            // First name, not raw email - matches Home's identity treatment
            // (CoupleHeader.tsx); still falls back to the full email when
            // no display name exists at all.
            const name = firstNameOf(c.otherUser.fullName, c.otherUser.email);
            const unread = c.unreadCount > 0;
            const lastMine = !!c.lastMessage && c.lastMessage.senderEmail === currentUserEmail;
            const attachmentIcon = c.lastMessage?.attachmentType ? ATTACHMENT_ICON[c.lastMessage.attachmentType] : null;
            // The conversation summary itself doesn't carry an avatar - cross-
            // referenced from the already-loaded directory (mentions.ts) by
            // email rather than widening the conversations response shape.
            const avatarFilename = directory.find((u) => u.email === c.otherUser.email)?.avatarFilename;
            return (
              <div
                key={c.id}
                className="chat-list-item d-flex align-items-center gap-2"
                role="listitem"
                onClick={() => navigate(`/chat/${c.id}`)}
              >
                <span className="chat-avatar flex-shrink-0" aria-hidden="true">
                  {avatarFilename ? (
                    <AvatarImage
                      filename={avatarFilename}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                      fallback={initials(name)}
                    />
                  ) : (
                    initials(name)
                  )}
                </span>
                <div className="min-w-0 flex-grow-1">
                  <div className="d-flex justify-content-between align-items-baseline gap-2">
                    <span className={`text-truncate min-w-0 ${unread ? "fw-bold" : "fw-semibold"}`}>{name}</span>
                    <span className={`chat-list-time flex-shrink-0 ${unread ? "chat-list-time-unread" : "muted"}`}>
                      {c.lastMessage ? formatRelativeTime(c.lastMessage.createdAt) : ""}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center gap-2">
                    <span className={`chat-list-preview small min-w-0 ${unread ? "chat-list-preview-unread" : "muted"}`}>
                      {c.lastMessage ? (
                        <>
                          {lastMine && <LastMessageTicks lastMessage={c.lastMessage} />}
                          {attachmentIcon && <i className={`bi ${attachmentIcon} chat-list-attachment-icon`} aria-hidden="true" />}
                          <span className="chat-list-preview-text text-truncate">{c.lastMessage.text || ""}</span>
                        </>
                      ) : (
                        <span className="muted">Say hello</span>
                      )}
                    </span>
                    {unread && (
                      <span className="chat-unread-badge flex-shrink-0">{c.unreadCount > 9 ? "9+" : c.unreadCount}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>,
    container,
  );
}

export function ChatListScreen() {
  return <ChatListBody />;
}
